import { Controller } from "@hotwired/stimulus"

// Search by photo (photo_searches/_drop): the camera in the header's search bar
// opens the panel; a photo dropped on it, chosen, or pasted while it's open,
// or a picture's link dropped or pasted, is sent at once. A picture dragged
// from another tab comes as a file in some browsers and as its link in others.
export default class extends Controller {
  static targets = ["panel", "form", "zone", "file", "url"]

  connect() {
    this.paste = (event) => this.pasted(event)
    this.escape = (event) => { if (event.key === "Escape") this.close() }
    this.outside = (event) => {
      if (this.isOpen && !this.panelTarget.contains(event.target) && !event.target.closest(".hdr-cam")) this.close()
    }
    if (this.hasPanelTarget && this.panelTarget.dataset.page) this.listen()
  }

  disconnect() { this.unlisten() }

  get isOpen() { return this.hasPanelTarget && this.panelTarget.classList.contains("is-open") }

  toggle(event) {
    event?.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.add("is-open")
    this.listen()
  }

  close() {
    if (!this.hasPanelTarget || this.panelTarget.dataset.page) return
    this.panelTarget.classList.remove("is-open")
    this.unlisten()
  }

  listen() {
    if (this.listening) return
    this.listening = true
    document.addEventListener("paste", this.paste)
    document.addEventListener("keydown", this.escape)
    document.addEventListener("click", this.outside)
  }

  unlisten() {
    this.listening = false
    document.removeEventListener("paste", this.paste)
    document.removeEventListener("keydown", this.escape)
    document.removeEventListener("click", this.outside)
  }

  over(event) {
    event.preventDefault()
    this.zoneTarget.classList.add("is-over")
  }

  leave() { this.zoneTarget.classList.remove("is-over") }

  drop(event) {
    event.preventDefault()
    this.leave()
    this.take(event.dataTransfer)
  }

  pasted(event) {
    // Typing or pasting into the link box is left alone; it's sent with Find it.
    if (event.target === this.urlTarget) return
    if (this.take(event.clipboardData)) event.preventDefault()
  }

  chosen() { if (this.fileTarget.files.length) this.send() }

  busy() { this.zoneTarget.classList.add("is-busy") }

  // A picture file, else a link, from a drop or a paste; true if there was one.
  take(data) {
    if (!data) return false
    const file = Array.from(data.files || []).find((f) => f.type.startsWith("image/"))
    if (file) {
      const files = new DataTransfer()
      files.items.add(file)
      this.fileTarget.files = files.files
      this.urlTarget.value = ""
      this.send()
      return true
    }
    const link = (data.getData("text/uri-list") || data.getData("text/plain") || "").split(/\s+/).find((s) => /^https?:\/\//i.test(s))
    if (link) {
      this.urlTarget.value = link
      this.send()
      return true
    }
    return false
  }

  send() {
    this.busy()
    this.formTarget.requestSubmit()
  }
}
